import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Sun, Eye, Shield, FileText, ExternalLink, Info, Cloud, Check } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { BBN_PHOTOS } from "@/lib/images";
import { PostHeroConversion } from "@/components/sections/PostHeroConversion";

/**
 * Landningssida för 3M Prestige 40 Exterior och övriga utvändiga
 * Prestige-varianter. Invändig Prestige omfattar endast Prestige 70.
 */

const exteriorSpecs = [
  { label: "Synligt ljus (VLT)", value: "51 %", icon: Eye },
  { label: "Solenergireduktion (TSER)", value: "56 %", icon: Sun },
  { label: "UV-blockering", value: "99,9 %", icon: Shield },
  { label: "Bländningsreduktion", value: "41 %", icon: Eye },
];

const faqs = [
  {
    q: "Finns 3M Prestige 40 Exterior för utvändig montering?",
    a: "Ja. 3M Prestige 40 Exterior är en utvändig film utan metall i filmen, med medium ljusgenomsläpp och mycket hög värmeavvisning. Det utvändiga sortimentet har även Prestige 70 Exterior och Prestige 90 Exterior. För invändig montering använder vi Prestige 70.",
  },
  {
    q: "När behövs utvändig Prestige istället för invändig?",
    a: "I 95 % av installationerna monteras filmen invändigt, skyddad mot väder, med längre livslängd och längst garanti. Utvändig Prestige (Exterior) väljs när invändig montering inte är möjlig: vissa LowE-belagda glas, tjocka fasadrutor eller när termisk stress riskerar skada isolerglaset. Vi verifierar glastyp och LowE-position innan rekommendation.",
  },
  {
    q: "Vad kostar 3M Prestige 40 Exterior monterat?",
    a: "Priset beror på fönsterytan, glastypen, fasadhöjden och åtkomsten. Ställning eller skylift kan påverka kostnaden. Vi mäter och lämnar ett fast pris efter det kostnadsfria platsbesöket.",
  },
  {
    q: "Vilken garanti gäller på Prestige Exterior?",
    a: "Utvändig Prestige har 7–10 års produktgaranti från 3M, jämfört med upp till 15 år för invändig. Skillnaden beror på UV- och väderpåverkan på utvändig sida. BBN Nordic lämnar dessutom 10 års garanti på installationsarbetet.",
  },
  {
    q: "Är Prestige 40 Exterior rätt val för söderfasad?",
    a: "Den kan vara det. Prestige 40 Exterior skyddar mot blekning och bländning och har mycket hög värmeavvisning. Prestige 70 Exterior och 90 Exterior släpper in mer ljus. Vi väljer film efter glastyp, hur mycket ljus du vill behålla och hur mycket sol fasaden får.",
  },
];

export const Route = createFileRoute("/3m-prestige-40-exterior")({
  head: () => ({
    meta: [
      { title: "3M Prestige 40 Exterior — utvändig solfilm, pris & montering | BBN Nordic" },
      {
        name: "description",
        content:
          "3M Prestige 40 Exterior och övriga utvändiga Prestige-filmer för söderfasader, kontor & LowE-glas. Auktoriserad 3M-installation i Sverige.",
      },
      { property: "og:title", content: "3M Prestige 40 Exterior" },
      {
        property: "og:description",
        content:
          "Utvändig Prestige-solfilm utan metall för solutsatta fasader. Mycket hög värmeavvisning och skydd mot blekning.",
      },
      { property: "og:url", content: "https://bbnnordic.se/3m-prestige-40-exterior" },
      { property: "og:image", content: BBN_PHOTOS.prestigeHero },
      { property: "og:type", content: "product" },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/3m-prestige-40-exterior" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "3M Prestige 40 Exterior solfilm",
          category: "Fönsterfilm / Premium-solfilm",
          description:
            "3M Prestige 40 Exterior för utvändig montering. Nanokeramisk multilagerteknik utan metaller.",
          image: BBN_PHOTOS.prestigeHero,
          brand: { "@type": "Brand", name: "3M" },
          manufacturer: { "@type": "Organization", name: "3M" },
          url: "https://bbnnordic.se/3m-prestige-40-exterior",
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "SEK",
            lowPrice: "1400",
            highPrice: "1900",
            priceSpecification: { "@type": "PriceSpecification", price: "1400", priceCurrency: "SEK" },
            availability: "https://schema.org/InStock",
            seller: { "@id": "https://bbnnordic.se/#organization" },
            areaServed: "SE",
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: "SE",
              returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "SEK" },
              shippingDestination: { "@type": "DefinedRegion", addressCountry: "SE" },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 5, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 14, unitCode: "DAY" },
              },
            },
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "6",
            bestRating: "5",
            worstRating: "1",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <PageHero
        eyebrow="3M PRESTIGE 40 EXTERIOR"
        title="3M Prestige 40 Exterior — utvändig solfilm för fasader som får för mycket sol"
        subtitle="Prestige 40 Exterior sätts på glasets utsida. Den har ingen metall i filmen, skyddar mot blekning och bländning och har mycket hög värmeavvisning."
        image={BBN_PHOTOS.prestigeHero}
        breadcrumbs={[{ label: "3M Prestige", to: "/3m-prestige" }, { label: "Prestige 40 Exterior" }]}
      />
      <PostHeroConversion />

      {/* Ärligt svar överst */}
      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <Reveal>
            <div className="rounded-2xl border border-gold/30 bg-gold/5 p-6 md:p-8">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold font-semibold mb-3">
                <Info size={14} /> Rakt svar först
              </div>
              <p className="text-foreground/90 leading-relaxed">
                <strong>3M Prestige 40 Exterior</strong> är en utvändig film. Det utvändiga Prestige-sortimentet
                har även Prestige 70 Exterior och Prestige 90 Exterior. För invändig montering
                använder vi Prestige 70.
              </p>
              <p className="mt-3 text-sm text-muted-foreground">
                Vi bedömer rätt monteringssida och variant vid det kostnadsfria platsbesöket, baserat
                på glastyp, LowE-position, fasadhöjd och garantibehov.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Två spec-block */}
      <section className="py-16 md:py-24 bg-navy-light border-y border-border">
        <div className="mx-auto max-w-7xl px-5 md:px-10 grid md:grid-cols-2 gap-8">
          <Reveal>
            <div className="rounded-2xl border border-gold/30 bg-card/60 p-7 h-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="rounded-lg bg-gold/15 p-2"><Cloud size={18} className="text-gold" /></div>
                <h2 className="font-display text-2xl text-foreground">Prestige 40 Exterior — utvändig</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                Prestige 40 Exterior sätts på glasets utsida. Den har medium ljusgenomsläpp, ingen
                metall i filmen och mycket hög värmeavvisning. 3M lämnar 10/7 års garanti.
              </p>
              <p className="mt-4 text-sm text-foreground/85">
                <strong>Pris:</strong> fast pris efter platsbesök, beroende på glastyp, fönsteryta och åtkomst.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-gold/20 bg-card/40 p-7 h-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="rounded-lg bg-gold/15 p-2"><Cloud size={18} className="text-gold" /></div>
                <h2 className="font-display text-2xl text-foreground">Prestige 50 Exterior — utvändig</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                Har väderbeständig acrylatlimning och används när invändig film inte är möjlig, till exempel
                LowE-belagda glas eller där termisk stress riskerar skada isolerglaset.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {exteriorSpecs.map((s) => (
                  <div key={s.label} className="rounded-xl bg-background/40 p-4">
                    <div className="font-display text-2xl text-gold-bright">{s.value}</div>
                    <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-foreground/85"><strong>Pris:</strong> 1 400–1 900 kr/m² monterat</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Datablad */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">OFFICIELLA DATABLAD</p>
            <h2 className="mt-3 text-3xl md:text-4xl">Se datablad för fönsterfilm</h2>
          </Reveal>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            {[
              {
                title: "Se alla datablad",
                href: "/datablad",
              },
            ].map((d) => (
              <a key={d.href} href={d.href}
                 className="glass p-5 flex items-center gap-4 group">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded bg-gold/15 text-gold shrink-0">
                  <FileText size={20} />
                </span>
                <div className="flex-1 font-semibold text-foreground">{d.title}</div>
                <ExternalLink size={16} className="text-gold group-hover:translate-x-1 transition-transform" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-navy-light border-t border-border">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <Reveal>
            <p className="section-number">VANLIGA FRÅGOR</p>
            <h2 className="mt-3 text-3xl md:text-4xl">3M Prestige 40 Exterior — vanliga frågor</h2>
          </Reveal>
          <div className="mt-8 space-y-4">
            {faqs.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.05}>
                <details className="glass p-5 group">
                  <summary className="cursor-pointer font-semibold text-foreground list-none flex justify-between items-start gap-4">
                    {f.q}
                    <span className="text-gold shrink-0 group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-muted-foreground font-light leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/offert" className="btn-glow inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 rounded-full font-semibold">
                Få kostnadsfri offert <ArrowRight size={16} />
              </Link>
              <Link to="/3m-prestige-70" className="inline-flex items-center gap-2 px-4 py-3 font-medium text-gold-bright hover:underline">
                Se även Prestige 70 →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
